/* eslint-disable @typescript-eslint/no-explicit-any */
import { shouldLog } from 'common/logrocket';
import {
  getCorrelationId,
  getIsFeatureToggleEnabled,
  getSessionId
} from 'communicator/store/selectors';
import { FetchResponse, FetchResponseValue, Headers } from 'src/types/fetch';
import { JSONObject } from 'src/types/utility';
import fetchLogEvent from './api/log-event';
import endpoints from './endpoints';
import getVbaToken from './get-vba-token';
import { logRecognizeNoVbaToken, logVbaSlaFailure, markEnd, markStart } from './instrumentation';
import parseApiError from './parse-api-error';
import serializeObject from './serialize';

let vbaTokenPromise: Promise<string | null> | undefined;

export const recognizeNoVba = getIsFeatureToggleEnabled('recognizeNoVba');
export let isVbaJsLoaded = typeof window.VAAP !== 'undefined';
// uncertain load t0 - check both customEvent & existing window.VAAP
if (recognizeNoVba) {
  window.addEventListener('vba_js_loaded', () => {
    isVbaJsLoaded = true;
  });
}

async function getAndRefreshVbaToken(): Promise<string | null> {
  const currentVbaPromise = vbaTokenPromise;
  // immediately pre-fetch vbaToken in anticipation of next api request
  vbaTokenPromise = getVbaToken();

  // 1st time through, this may be undefined.
  if (!currentVbaPromise) {
    if (!window.VAAP) {
      await new Promise((resolve, reject) => {
        // interval to check for VAAP - max 10s
        let max = 100;
        const interval = setInterval(() => {
          if (window.VAAP) {
            clearInterval(interval);
            resolve(void 0);
          }

          if (max-- <= 0) {
            clearInterval(interval);
            logVbaSlaFailure();
            reject(new Error('dependency failed to load'));
          }
        }, 100);
      });

      vbaTokenPromise = undefined;
    }

    return getAndRefreshVbaToken();
  }

  return currentVbaPromise;
}

const logrocketEnabled = getIsFeatureToggleEnabled('enableLogrocket');

export const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
};

export const handleJsonResponse = (res: Response): FetchResponse<any> => {
  const ok = res.ok;

  const headers: Headers = {};
  res.headers.forEach((v, k) => {
    headers[k] = v;
  });

  // Returns null if status is not in the range 200-299
  if (!ok) {
    return res
      .json()
      .then(data => ({ ...parseApiError({ headers, httpStatus: res.status, ...data }), headers }))
      .catch(() => ({ ...parseApiError({ headers, httpStatus: res.status }), headers }));
  }

  //START GENAI@GHCOPILOT
  return res
    .json()
    .then(data => {
      const cardBrand = (data && data.maskedCard) || null;
      const enrollFlow = cardBrand !== 'VISA' ? 'UCTP' : 'CTP';
      return {
        data,
        enrollFlow,
        headers,
        ok
      };
    })
    .catch(() => ({ data: null, enrollFlow: 'NO_MASKED_CARD', headers, ok }));
  //END GENAI@GHCOPILOT
};

export type FetchOptions = {
  body?: JSONObject;
  headers?: Headers;
  method?: string;
  url: string;
  mark?: string;
  waitForVbaToken?: boolean;
  signal?: AbortSignal;
};

let recordingURL: string;
export default async function fetchReq(options: FetchOptions): FetchResponse<any, any, Headers> {
  let vbaToken;

  if (logrocketEnabled) {
    window.LogRocket &&
      window.LogRocket.getSessionURL((sessionURL: string) => {
        // eslint-disable-next-line no-console
        shouldLog && console.info(`-------- [LR] fetch with sessionUrl: ${sessionURL}`);
        recordingURL = sessionURL;
      });
  }

  const { url, body, mark, method, headers, waitForVbaToken } = options;

  const sessionId = getSessionId() || 'allowme';
  const finalHeaders: Headers = {
    ...defaultHeaders,
    dfpSessionId: sessionId,
    'X-CORRELATION-ID': getCorrelationId(),
    'X-LogRocket-URL': recordingURL,
    'X-THMID': sessionId,
    ...headers
  };

  const finalBody =
    finalHeaders['Content-Type'] === 'application/x-www-form-urlencoded'
      ? serializeObject(body)
      : JSON.stringify(body);

  mark && markStart(`get_vba_token_${mark}`);

  if (
    waitForVbaToken === false && // /recognize call only
    recognizeNoVba === true // FT is "ON"
  ) {
    // attach VBA token for /recognize if available
    if (typeof window.VAAP !== 'undefined') {
      vbaToken = await getAndRefreshVbaToken();
    }
  } else {
    // average path
    vbaToken = await getAndRefreshVbaToken();
  }

  mark && markEnd(`get_vba_token_${mark}`);

  if (typeof vbaToken === 'string') {
    finalHeaders['v-c-vaap-token'] = vbaToken;
  } else {
    logRecognizeNoVbaToken();
  }

  mark && markStart(mark);
  let response;
  try {
    const fetchResponse = await fetch(url, {
      body: finalBody,
      credentials: 'same-origin',
      headers: finalHeaders as Record<string, string>,
      method,
      signal: options.signal
    });

    response = await handleJsonResponse(fetchResponse);
  } catch (error) {
    // pushError to CAL
    if (url !== endpoints.eventLogging) {
      fetchLogEvent({
        correlationId: getCorrelationId(),
        request: {
          E: 'VISA_FETCH_ERROR',
          LL: 'ERROR',
          ERR: error
        }
      });
    }

    // eslint-disable-next-line no-console
    console.error(error);
    mark && markEnd(mark, { failure: true });
    return error as FetchResponseValue;
  }

  mark && markEnd(mark, { failure: !response.ok });
  return response;
}

// Fetch API in Edge v15 and v16 does not normalize the response header names to
// lower case as all other browsers do. Need to check both upper and lower case
// on the object to ensure we get the value.
export function getHeader(response: FetchResponseValue<unknown>, name: string) {
  return (
    response.headers[name] ||
    response.headers[name.toUpperCase()] ||
    response.headers[name.toLowerCase()]
  );
}
